import React, { useMemo, useRef, useEffect, useState } from 'react';
import { DomainHit } from '@/lib/trustHub/v1/types';
import { generateHierarchicalTreeMapLayout, TreeMapCell } from '@/lib/trustHub/v1/treemapUtils';
import { TreeMapBox } from './TreeMapBox';
import { TreeMapTooltip } from './TreeMapTooltip';
import { TreeMapLegend } from './TreeMapLegend';
import { MousePointer2 } from 'lucide-react';

interface DomainTreeMapProps {
  domains: DomainHit[];
  queries: string[];
  totalQueries: number;
  maxDomains?: number;
  consideredWeight?: number;
  onQueryClick?: (query: string) => void;
}

export const DomainTreeMap: React.FC<DomainTreeMapProps> = ({
  domains,
  queries,
  totalQueries,
  maxDomains = 30,
  consideredWeight = 0.25,
  onQueryClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 1000, height: 600 });
  const [hoveredDomain, setHoveredDomain] = useState<string | null>(null);
  
  // Measure container and update dimensions
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        const safeWidth = Math.max(320, Math.floor(width || 0));
        // Maintain 5:3 aspect ratio, with minimum height of 400px
        const height = Math.max(400, Math.floor((safeWidth * 3) / 5));
        setDimensions({ width: safeWidth, height });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  
  // Generate hierarchical treemap layout
  const cells = useMemo(() => {
    const topDomains = domains.slice(0, maxDomains);
    return generateHierarchicalTreeMapLayout(
      topDomains,
      queries,
      totalQueries,
      consideredWeight,
      dimensions.width,
      dimensions.height
    );
  }, [domains, queries, totalQueries, maxDomains, consideredWeight, dimensions]);

  const domainCells = useMemo(() => cells.filter(c => !c.isGroup), [cells]);

  return (
    <div className="space-y-4">
      {/* Summary stats bar */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex gap-6 text-muted-foreground">
          <span>Showing <span className="font-semibold text-foreground">{domainCells.length}</span> domains</span>
          <span><span className="font-semibold text-foreground">{domainCells.reduce((sum, c) => sum + c.usedCount, 0)}</span> total citations</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <MousePointer2 className="h-3.5 w-3.5" />
          <span>Click query headers to filter</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gradient-to-br from-purple-600 to-purple-700"></div>
          <span>Claude-dominant</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gradient-to-br from-indigo-600 to-violet-600"></div>
          <span>Balanced</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gradient-to-br from-blue-600 to-cyan-600"></div>
          <span>OpenAI-dominant</span>
        </div>
      </div>
      
      {/* Main treemap container */}
      <div
        ref={containerRef}
        className="relative bg-background/50 rounded-lg border-2 border-border overflow-hidden"
        style={{ height: `${dimensions.height}px` }}
      >
        {cells.map((cell) => (
          cell.isGroup ? (
            <TreeMapBox
              key={`group-${cell.domain}`}
              cell={cell}
              isHovered={false}
              onHover={() => {}}
              onClick={() => onQueryClick?.(cell.query || cell.domain)}
            />
          ) : (
            <TreeMapTooltip key={cell.domain} cell={cell}>
              <TreeMapBox
                cell={cell}
                isHovered={hoveredDomain === cell.domain}
                onHover={(hovered) => setHoveredDomain(hovered ? cell.domain : null)}
              />
            </TreeMapTooltip>
          )
        ))}
      </div>
    </div>
  );
};
